import { useCallback, useEffect, useMemo, useState } from 'react';

import { PRODUCT_TYPES_OPTIONS } from '~/consts/agentHub';
import type { CommissionsConditionTypes, RuleCondition } from '~/consts/agentHub';

import { useGetAgenciesById } from '~/hooks/apis/agent/useGetAgenciesById';
import { useGetUsersSummaryByIds } from '~/hooks/apis/agent/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import type { Commission } from '~/types/services/agentHub';

import { areCommissionOptionsEqual, normalizeApiConditionType } from '~/utils/agentHub';
import { arrayToObject } from '~/utils/arrayUtils';
import { parseCommissionRuleBoundaryToSydneyDate } from '~/utils/commissionDates';

import {
  CONDITION_OPERATORS,
  GROUP_OPERATORS,
  VALID_COMMISSION_TYPES,
  createFormId,
  getCommissionGroupInitialState,
  getEmptyCommissionFormState,
} from './CommissionDialog.types';
import type {
  CommissionConditionForm,
  CommissionConditionOperator,
  CommissionFormState,
  CommissionGroupOperator,
  CommissionTypeChoice,
} from './CommissionDialog.types';

type InputOptionData = string | number | Record<string, unknown>;
type InputOptionFunc = (data: InputOptionData) => { value: string; label: string };

interface UseCommissionFormOptions {
  value?: CommissionFormState;
  onChange?: (next: CommissionFormState) => void;
  data?: Commission;
  isOpen?: boolean;
}

export function useCommissionForm({ value, onChange, data, isOpen = true }: UseCommissionFormOptions = {}) {
  const isControlled = value !== undefined && onChange !== undefined;
  const [internalValue, setInternalValue] = useState<CommissionFormState>(getEmptyCommissionFormState);
  const formValue = isControlled ? value : internalValue;
  const brand = useCurrentBrand();

  const setFormValue = useCallback(
    (next: CommissionFormState | ((current: CommissionFormState) => CommissionFormState)) => {
      if (isControlled) {
        const resolved = typeof next === 'function' ? next(value) : next;
        onChange(resolved);
        return;
      }
      setInternalValue(next);
    },
    [isControlled, onChange, value],
  );

  const groupedProductedTypes = useMemo(() => {
    return arrayToObject(PRODUCT_TYPES_OPTIONS, (productType) => productType);
  }, []);

  const getRuleTypeFunction = useCallback(
    (type: string): InputOptionFunc => {
      switch (type) {
        case 'productType':
          return (optionData) => {
            const key = String(optionData);
            return { value: key, label: groupedProductedTypes[key] ?? key };
          };
        case 'flightCarrier':
          return (optionData) => {
            if (optionData != null && typeof optionData === 'object' && !Array.isArray(optionData)) {
              const option = optionData as { code?: string; name?: string; airlineCode?: string; airlineName?: string };
              const code = option.code ?? option.airlineCode ?? '';
              const name = option.name ?? option.airlineName ?? code;
              return { value: code, label: name };
            }
            const code = String(optionData);
            return { value: code, label: code };
          };
        default:
          return (optionData) => {
            if (typeof optionData === 'object' && optionData != null && !Array.isArray(optionData)) {
              const firstPrimitive = Object.values(optionData).find(
                (item): item is string | number => typeof item === 'string' || typeof item === 'number',
              );
              const normalized = firstPrimitive != null ? String(firstPrimitive) : '';
              return { value: normalized, label: normalized };
            }
            const normalized = String(optionData);
            return { value: normalized, label: normalized };
          };
      }
    },
    [groupedProductedTypes],
  );

  const agencyIds = useMemo(() => {
    if (!data?.commissionGroups || !isOpen) {
      return [];
    }
    return data.commissionGroups.flatMap((group) =>
      group.conditions.filter((condition) => condition.type === 'agency').map((condition) => condition.value),
    );
  }, [data?.commissionGroups, isOpen]);

  const agentIds = useMemo(() => {
    if (!data?.commissionGroups || !isOpen) {
      return [];
    }
    return data.commissionGroups.flatMap((group) =>
      group.conditions.filter((condition) => condition.type === 'agent').map((condition) => condition.value),
    );
  }, [data?.commissionGroups, isOpen]);

  const { data: agenciesData, isFetching: isLoadingAgencies } = useGetAgenciesById(agencyIds);
  const { data: agentsMap, isFetching: isLoadingAgents } = useGetUsersSummaryByIds(agentIds, brand);

  const isLoadingConditions = isLoadingAgencies || isLoadingAgents;

  const resetForm = useCallback(() => {
    setFormValue(getEmptyCommissionFormState());
  }, [setFormValue]);

  useEffect(() => {
    if (isControlled || !isOpen || data) {
      return;
    }
    setFormValue(getEmptyCommissionFormState());
  }, [data, isControlled, isOpen, setFormValue]);

  useEffect(() => {
    if (isControlled || !data || !isOpen) {
      return;
    }

    const agencyLabels = new Map(agenciesData?.map((agency) => [agency.id, agency.name]) ?? []);
    const agentLabels = new Map(
      Array.from(agentsMap?.values() ?? []).map((agent: { id_member: string; full_name: string; email: string }) => [
        agent.id_member,
        `${agent.full_name} [${agent.email}]`,
      ]),
    );

    const nextGroups =
      data.commissionGroups?.map((group) => {
        const conditions: Array<CommissionConditionForm> = [];
        group.conditions.forEach((condition) => {
          const type = normalizeApiConditionType(condition.type);
          if (!type) {
            return;
          }
          const option =
            type === 'agency'
              ? { value: condition.value, label: agencyLabels.get(condition.value) ?? condition.value }
              : type === 'agent'
                ? { value: condition.value, label: agentLabels.get(condition.value) ?? condition.value }
                : getRuleTypeFunction(type)(condition.value);
          const operator = CONDITION_OPERATORS.includes(condition.operator as CommissionConditionOperator)
            ? (condition.operator as CommissionConditionOperator)
            : 'eq';

          conditions.push({
            id: condition.id,
            type,
            operator,
            value: [option],
          });
        });

        return {
          id: group.id,
          operator: GROUP_OPERATORS.includes(group.operator as CommissionGroupOperator)
            ? (group.operator as CommissionGroupOperator)
            : 'AND',
          conditions,
        };
      }) ?? [];

    const commissionValueLimits = (data.commissionValueLimits ?? []).reduce<Record<string, string>>((acc, limit) => {
      acc[limit.region] = String(limit.maxAmount);
      return acc;
    }, {});

    setFormValue({
      description: data.description ?? '',
      commissionPercentage: data.commissionPercentage != null ? String(data.commissionPercentage) : '',
      commissionType: VALID_COMMISSION_TYPES.has(data.type as CommissionTypeChoice)
        ? (data.type as CommissionTypeChoice)
        : '',
      regions: data.regions ?? [],
      startDate: data.startDate ? parseCommissionRuleBoundaryToSydneyDate(data.startDate) : null,
      endDate: data.endDate ? parseCommissionRuleBoundaryToSydneyDate(data.endDate) : null,
      shouldIncludeLimit: data.limit != null,
      limit: data.limit != null ? String(data.limit) : '',
      priorityInput: data.priority != null ? String(data.priority) : '',
      canHaveAdditionalCommissions: Boolean(data.canHaveAdditionalCommissions),
      includesTaxesAndFees: data.includesTaxesAndFees ?? true,
      includesMargin: data.includesMargin ?? true,
      commissionValueLimits,
      commissionGroups: nextGroups.reduce((acc, group) => {
        acc[group.operator] = group;
        return acc;
      }, getCommissionGroupInitialState()),
    });
  }, [
    agencyIds.length,
    agenciesData,
    agentIds.length,
    agentsMap,
    data,
    getRuleTypeFunction,
    isControlled,
    isOpen,
    setFormValue,
  ]);

  const addCondition = useCallback(
    (operator: CommissionGroupOperator, conditionId: string, newCondition: RuleCondition) => {
      setFormValue((current) => {
        const group = current.commissionGroups[operator];
        const condition = group.conditions.find((currentCondition) => currentCondition.id === conditionId);
        if (!condition) {
          return current;
        }
        if (condition.type === newCondition.type && areCommissionOptionsEqual(condition.value, newCondition.value)) {
          return current;
        }
        return {
          ...current,
          commissionGroups: {
            ...current.commissionGroups,
            [operator]: {
              ...group,
              conditions: group.conditions.map((currentCondition) =>
                currentCondition.id === conditionId
                  ? {
                      ...currentCondition,
                      type: newCondition.type,
                      value: [...newCondition.value],
                    }
                  : currentCondition,
              ),
            },
          },
        };
      });
    },
    [setFormValue],
  );

  const handleChangeCondition = useCallback(
    (groupId: CommissionGroupOperator, conditionId: string, type: CommissionsConditionTypes) => {
      setFormValue((current) => {
        const group = current.commissionGroups[groupId];
        const condition = group.conditions.find((currentCondition) => currentCondition.id === conditionId);
        if (!condition) {
          return current;
        }
        if (condition.type === type && condition.value.length === 0) {
          return current;
        }
        return {
          ...current,
          commissionGroups: {
            ...current.commissionGroups,
            [groupId]: {
              ...group,
              conditions: group.conditions.map((currentCondition) =>
                currentCondition.id === conditionId
                  ? {
                      ...currentCondition,
                      type,
                      value: [],
                    }
                  : currentCondition,
              ),
            },
          },
        };
      });
    },
    [setFormValue],
  );

  const handleConditionOperatorChange = useCallback(
    (groupOperator: CommissionGroupOperator, conditionId: string, operator: CommissionConditionOperator) => {
      setFormValue((current) => ({
        ...current,
        commissionGroups: {
          ...current.commissionGroups,
          [groupOperator]: {
            ...current.commissionGroups[groupOperator],
            conditions: current.commissionGroups[groupOperator].conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, operator } : condition,
            ),
          },
        },
      }));
    },
    [setFormValue],
  );

  const handleClearCondition = useCallback(
    (operator: CommissionGroupOperator, conditionId: string) => {
      setFormValue((current) => ({
        ...current,
        commissionGroups: {
          ...current.commissionGroups,
          [operator]: {
            ...current.commissionGroups[operator],
            conditions: current.commissionGroups[operator].conditions.filter(
              (condition) => condition.id !== conditionId,
            ),
          },
        },
      }));
    },
    [setFormValue],
  );

  const handleAppendCondition = useCallback(
    (operator: CommissionGroupOperator) => {
      setFormValue((current) => ({
        ...current,
        commissionGroups: {
          ...current.commissionGroups,
          [operator]: {
            ...current.commissionGroups[operator],
            conditions: [
              ...current.commissionGroups[operator].conditions,
              {
                id: createFormId(),
                type: 'productType',
                operator: 'eq',
                value: [],
              },
            ],
          },
        },
      }));
    },
    [setFormValue],
  );

  return {
    value: formValue,
    onChange: setFormValue,
    resetForm,
    isLoadingConditions,
    addCondition,
    handleChangeCondition,
    handleConditionOperatorChange,
    handleClearCondition,
    handleAppendCondition,
  };
}
